import type { ReactNode } from "react";
import type { ItemAyuda } from "@/ui/AyudaVista";
import { ACTIVIDAD_PILL } from "@/lib/actividad";
import { ESTADO_IMEI_LABEL, ESTADO_IMEI_PILL, type EstadoImeiUI } from "@/lib/imei";

/**
 * «Qué significa cada ficha» de un caso, para la Ayuda de cualquier lista de
 * casos (Casos, Panel, El proveedor contestó).
 *
 * `components/ayuda/vistas/` guarda el contenido del botón «Ayuda» de cada
 * vista (`ui/AyudaVista`); el manual largo es el resto de `components/ayuda/`.
 *
 * Las fichas viven en `BadgesCaso` y se ven igual en todas las listas; su
 * explicación también va en un solo lugar. Antes era el tooltip de cada ficha,
 * invisible en el celular. Si agregas o cambias una ficha, cambia esto.
 */

function Ficha({ cls, children }: { cls: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] ${cls}`}>
      {children}
    </span>
  );
}

const IMEIS: EstadoImeiUI[] = ["OK", "REV", "CRIT", "CLI", "PROVEEDOR"];

export const FICHA_ACTIVIDAD: ItemAyuda = {
    label: (
      <span className="flex flex-wrap gap-1">
        <Ficha cls={ACTIVIDAD_PILL.ok}>hoy</Ficha>
        <Ficha cls={ACTIVIDAD_PILL.por_quemarse}>hace 4 d</Ficha>
        <Ficha cls={ACTIVIDAD_PILL.quemado}>hace 9 d</Ficha>
      </span>
    ),
    text: (
      <>
        Días desde el último movimiento del caso (una nota o un cambio). <b>Verde</b>: con
        movimiento reciente. <b>Ámbar</b>: por quemarse, atiéndelo antes de que se queme.{" "}
        <b>Rojo</b>: quemado, o ya escalado y la administración recibió el aviso. Los días de
        cada color se ajustan en Configuración → Tiempos y escalación. Los casos cerrados no la
        llevan.
      </>
    ),
  };

export const FICHA_TE_ESPERAN: ItemAyuda = {
    label: <Ficha cls="uppercase tracking-wide border-amber-500/30 bg-amber-500/10 text-amber-300">⏳ te esperan</Ficha>,
    text: (
      <>
        Un compañero te mencionó en el caso y falta tu respuesta. <b>Ámbar</b>: no la has
        abierto. <b>Gris</b>: ya la viste, pero sigue esperando que contestes. Se quita sola
        cuando escribes en el caso (nota, correo o respuesta al proveedor).
      </>
    ),
  };

export const FICHA_POSESION: ItemAyuda = {
    label: (
      <span className="flex flex-wrap gap-1">
        <Ficha cls="uppercase tracking-wide border-sky-500/30 bg-sky-500/10 text-sky-300">⏳ Cliente</Ficha>
        <Ficha cls="uppercase tracking-wide border-orange-500/30 bg-orange-500/10 text-orange-300">📦 Proveedor</Ficha>
      </span>
    ),
    text: "Quién tiene la pelota cuando NO somos nosotros: esperamos al cliente o el caso está con el proveedor. Si no sale ninguna, la tenemos nosotros.",
  };

export const FICHA_CRITICO: ItemAyuda = {
    label: <Ficha cls="uppercase tracking-wide border-red-500/30 bg-red-500/10 text-red-300">⚠ crítico</Ficha>,
    text: "Al menos un equipo del caso está marcado como Crítico.",
  };

export const FICHA_YA_CONTESTO: ItemAyuda = {
    label: <Ficha cls="border-accent-500/40 bg-accent-500/10 text-accent-300">⏳ Ya contestó · 1 d</Ficha>,
    text: (
      <>
        El proveedor (Jimi o Queclink) contestó en su ticket y todavía no le respondemos; el
        número son los días desde su respuesta. Se pone <b>ámbar</b> a los 2 días y{" "}
        <b>roja</b> a los 5. Se quita al responderle, al cerrar el caso o al darlo por
        atendido con «⏳ el proveedor contestó», en el encabezado del caso.
      </>
    ),
  };

export const FICHA_TICKET: ItemAyuda = {
    label: (
      <span className="flex flex-wrap gap-1">
        <Ficha cls="font-mono border-teal-500/30 bg-teal-500/10 text-teal-300">🎫 Jimi</Ficha>
        <Ficha cls="font-mono border-sky-500/30 bg-sky-500/10 text-sky-300">🎫 Queclink</Ficha>
      </span>
    ),
    text: "El caso tiene ticket abierto con ese proveedor. El número del ticket y su estado allá están en el encabezado del caso.",
  };

export const FICHA_IMEIS: ItemAyuda = {
    label: (
      <span className="flex flex-wrap gap-1">
        {IMEIS.map((e) => (
          <Ficha key={e} cls={`font-mono uppercase ${ESTADO_IMEI_PILL[e]}`}>
            {e}
          </Ficha>
        ))}
      </span>
    ),
    text: (
      <>
        Los equipos (IMEI) del caso, con su estado:{" "}
        {IMEIS.map((e, i) => (
          <span key={e}>
            <b>{e}</b> {ESTADO_IMEI_LABEL[e].toLowerCase()}
            {i < IMEIS.length - 1 ? " · " : ". "}
          </span>
        ))}
        Si nadie lo marca a mano, el estado cambia solo con los días sin movimiento (OK, luego
        en revisión y luego crítico). Cuando son más de tres, toca «+N más» para ver el resto.
      </>
    ),
  };

/** Todas, en el orden en que salen en la lista de Casos. Las listas que pintan
 *  solo algunas (el Panel, «El proveedor contestó») arman su arreglo con las
 *  sueltas: explicar una ficha que esa vista no pinta solo confunde. */
export const FICHAS_CASO: ItemAyuda[] = [
  FICHA_ACTIVIDAD,
  FICHA_TE_ESPERAN,
  FICHA_POSESION,
  FICHA_CRITICO,
  FICHA_YA_CONTESTO,
  FICHA_TICKET,
  FICHA_IMEIS,
];
